import {
  FileCheck,
  FileText,
  Mail,
  MessageSquare,
  NotebookPen,
  Phone,
  Scale,
  Users,
  type LucideIcon,
} from "lucide-react";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import type { Artifact } from "../../shared/customer-context-schemas";
import {
  currentDecisions,
  HUB_RECORD_LABELS,
  type HubDocument,
  type HubRecord,
  type HubRecordType,
} from "../../shared/tool-hubs";
import { artifactPreviewHref } from "./api";

const RECORD_ICONS: Record<HubRecordType, LucideIcon> = {
  call: Phone,
  meeting: Users,
  email: Mail,
  text: MessageSquare,
  note: NotebookPen,
  decision: Scale,
};
const DIRECTION_LABELS = {
  inbound: "From the customer",
  outbound: "From us",
  internal: "Internal",
};

export type TimelineGroup = "calls" | "messages" | "notes" | "documents";
const RECORD_GROUPS: Record<HubRecordType, TimelineGroup> = {
  call: "calls",
  meeting: "calls",
  email: "messages",
  text: "messages",
  note: "notes",
  decision: "notes",
};

export type TimelineEntry = {
  key: string;
  group: TimelineGroup;
  at: number;
  icon: LucideIcon;
  label: string;
  title: string;
  badges: string[];
  record?: HubRecord;
  href?: string;
  external?: string;
};

const signedAt = (day: string) => Math.floor(Date.parse(`${day}T12:00:00Z`) / 1000);

function recordEntry(record: HubRecord, current: ReadonlySet<string>): TimelineEntry {
  const badges: string[] = [];
  if (record.direction) badges.push(DIRECTION_LABELS[record.direction]);
  if (record.type === "decision")
    badges.push(
      record.origin === "agent"
        ? "Proposed by an agent"
        : current.has(record.id)
          ? "Current"
          : "Replaced",
    );
  if (record.followUpOn) badges.push(`Follow up by ${record.followUpOn}`);
  if (record.artifactIds.length) badges.push("File attached");
  return {
    key: record.id,
    group: RECORD_GROUPS[record.type],
    at: record.occurredAt,
    icon: RECORD_ICONS[record.type],
    label: HUB_RECORD_LABELS[record.type],
    title: record.title,
    badges,
    record,
  };
}

function documentEntries(document: HubDocument, slug: string): TimelineEntry[] {
  const label = COMMERCIAL_DOCUMENT_LABELS[document.kind];
  const entries: TimelineEntry[] = [
    {
      key: `${document.id}:saved`,
      group: "documents",
      at: document.updatedAt,
      icon: FileText,
      label,
      title: `${document.title} · ${document.reference}`,
      badges: [`Version ${document.revision}`],
      href: `/customers/${slug}/documents/${document.id}`,
    },
  ];
  if (document.filing)
    entries.push({
      key: `${document.id}:signed`,
      group: "documents",
      at: signedAt(document.filing.signedOn),
      icon: FileCheck,
      label: "Signed",
      title: `${document.reference} signed and filed`,
      badges: [`Version ${document.filing.revision}`],
      href: `/customers/${slug}/documents`,
    });
  return entries;
}

export function buildTimeline(input: {
  records: readonly HubRecord[];
  artifacts: readonly Artifact[];
  documents: readonly HubDocument[];
  organizationId: string;
  slug: string;
}): TimelineEntry[] {
  const current = new Set(currentDecisions(input.records).map((record) => record.id));
  const attached = new Set(input.records.flatMap((record) => record.artifactIds));
  return [
    ...input.records.map((record) => recordEntry(record, current)),
    ...input.artifacts
      .filter((artifact) => !attached.has(artifact.id))
      .map((artifact) => ({
        key: artifact.id,
        group: "documents" as const,
        at: artifact.metadata.sourceCreatedAt ?? artifact.ingestedAt,
        icon: FileText,
        label: "File",
        title: artifact.metadata.title,
        badges: [],
        external: artifactPreviewHref(input.organizationId, artifact.id),
      })),
    ...input.documents.flatMap((document) => documentEntries(document, input.slug)),
  ].sort((a, b) => b.at - a.at);
}
